import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  X, Save, Plus, Search, Loader2, GitBranch, Package,
  Trash2, Link2, Link2Off,
} from 'lucide-react';
import { Combobox } from './Combobox';
import AddRebrandModal, { RebrandSelection } from './AddRebrandModal';
import { CatEscluseBadge } from './CatEscluseBadge';
import { toast } from 'sonner';
import {
  fetchMachineArticles, fetchArticles, createAssociations, deleteAssociations,
  fetchMachineRebrands, addRebrandChild, removeRebrandChild, updateMachine,
  MachineRow, MachineArticle, RebrandInfo, RebrandMachine, MarcaOption,
} from '../api';
import {
  Popover as PopoverRoot,
  PopoverTrigger as PopoverTriggerRoot,
  PopoverContent as PopoverContentRoot,
} from '../../../../components/ui/popover';
import {
  Command as CommandRoot,
  CommandInput as CommandInputRoot,
  CommandList as CommandListRoot,
  CommandEmpty as CommandEmptyRoot,
  CommandItem as CommandItemRoot,
} from '../../../../components/ui/command';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../../components/ui/alert-dialog';

/* ── Shadcn wrappers (TS-friendly) ─────────────────────────────── */
const Popover = PopoverRoot as React.FC<{ open?: boolean; onOpenChange?: (o: boolean) => void; children?: React.ReactNode }>;
const PopoverTrigger = PopoverTriggerRoot as React.FC<{ asChild?: boolean; children?: React.ReactNode }>;
const PopoverContent = PopoverContentRoot as React.FC<{ className?: string; align?: 'start' | 'center' | 'end'; sideOffset?: number; children?: React.ReactNode }>;
const Command = CommandRoot as React.FC<{ className?: string; shouldFilter?: boolean; children?: React.ReactNode }>;
const CommandInput = CommandInputRoot as React.FC<{ placeholder?: string; value?: string; onValueChange?: (v: string) => void; autoFocus?: boolean }>;
const CommandList = CommandListRoot as React.FC<{ className?: string; children?: React.ReactNode }>;
const CommandEmpty = CommandEmptyRoot as React.FC<{ children?: React.ReactNode }>;
const CommandItem = CommandItemRoot as React.FC<{ value?: string; onSelect?: () => void; className?: string; children?: React.ReactNode }>;

interface Props {
  machine: MachineRow;
  onClose: () => void;
  marche: MarcaOption[];
  tipologie: { code: string; label: string }[];
  onMachineUpdated?: () => void;
}

/* ─────────────────────────────────────────────────────────────────
   MachineDetailModal
───────────────────────────────────────────────────────────────── */
export default function MachineDetailModal({ machine, onClose, marche, tipologie, onMachineUpdated }: Props) {

  /* ── Edit state ── */
  const [editBrand, setEditBrand] = useState<string | null>(
    () => marche.find(m => m.label === machine.brand)?.code ?? null,
  );
  const [editTipo, setEditTipo] = useState<string | null>(machine.type ?? null);
  const [editNome, setEditNome] = useState(machine.model);
  const [saving, setSaving] = useState(false);

  const isDirty = useMemo(() => {
    const brandLabel = marche.find(m => m.code === editBrand)?.label ?? '';
    return (
      editNome !== machine.model ||
      brandLabel !== machine.brand ||
      (editTipo ?? '') !== (machine.type ?? '')
    );
  }, [editBrand, editTipo, editNome, machine, marche]);

  /* ── Rebrands ── */
  const [rebrandInfo, setRebrandInfo] = useState<RebrandInfo | null>(null);
  const [rebrandLoading, setRebrandLoading] = useState(true);
  const [addChildOpen, setAddChildOpen] = useState(false);
  const [addParentOpen, setAddParentOpen] = useState(false);

  /* Logica mutualmente esclusiva:
     - canHaveChildren = la macchina non ha già un padre rebrand
     - canHaveParent   = la macchina non ha già figli rebrand */
  const canHaveChildren = useMemo(() => !rebrandInfo?.parent, [rebrandInfo]);
  const canHaveParent   = useMemo(() => !(rebrandInfo?.children?.length), [rebrandInfo]);

  /* ── Articles ── */
  const [articles, setArticles] = useState<MachineArticle[]>([]);
  const [artLoading, setArtLoading] = useState(true);
  const [artQuery, setArtQuery] = useState('');
  const [artAddOpen, setArtAddOpen] = useState(false);
  const [artAddQuery, setArtAddQuery] = useState('');
  const [artAddResults, setArtAddResults] = useState<{ id: string; primary: string; secondary: string }[]>([]);
  const [artAddLoading, setArtAddLoading] = useState(false);
  const [artAddBusy, setArtAddBusy] = useState(false);
  const [confirmRemoveCodes, setConfirmRemoveCodes] = useState<string[] | null>(null);
  const [removing, setRemoving] = useState(false);
  const artAddTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* Load on mount */
  useEffect(() => {
    loadRebrands();
    loadArticles();
  }, [machine.id]); // eslint-disable-line

  /* Article add search debounce */
  useEffect(() => {
    if (!artAddOpen) return;
    if (artAddTimer.current) clearTimeout(artAddTimer.current);
    if (artAddQuery.length < 2) { setArtAddResults([]); return; }
    setArtAddLoading(true);
    artAddTimer.current = setTimeout(() => {
      fetchArticles({ search: artAddQuery, pageSize: 20 })
        .then(r => setArtAddResults(r.items.map(a => ({ id: a.sku, primary: a.desc, secondary: a.brand || '' }))))
        .catch(() => setArtAddResults([]))
        .finally(() => setArtAddLoading(false));
    }, 250);
    return () => { if (artAddTimer.current) clearTimeout(artAddTimer.current); };
  }, [artAddQuery, artAddOpen]);

  /* ── Loaders ── */
  function loadRebrands() {
    setRebrandLoading(true);
    fetchMachineRebrands(machine.id)
      .then(setRebrandInfo)
      .catch(() => setRebrandInfo({ parent: null, children: [] }))
      .finally(() => setRebrandLoading(false));
  }

  function loadArticles() {
    setArtLoading(true);
    fetchMachineArticles(machine.id)
      .then(setArticles)
      .catch(() => setArticles([]))
      .finally(() => setArtLoading(false));
  }

  /* ── Handlers ── */
  async function handleSave() {
    setSaving(true);
    try {
      const brandLabel = marche.find(m => m.code === editBrand)?.label;
      await updateMachine(machine.id, {
        brand: brandLabel,
        tipo: editTipo ?? undefined,
        nome: editNome || undefined,
      });
      toast.success('Macchina aggiornata');
      onMachineUpdated?.();
    } catch (e: any) {
      toast.error('Errore: ' + (e?.response?.data?.detail ?? e?.message ?? 'unknown'));
    } finally { setSaving(false); }
  }

  /* Aggiunge uno o più figli — aggiornamento diretto dello stato (no flicker) */
  async function handleAddChild(selections: RebrandSelection[], categorieEscluse: string[]) {
    await Promise.all(selections.map(sel => addRebrandChild(machine.id, sel.id, categorieEscluse)));
    const n = selections.length;
    toast.success(n === 1 ? 'Modello equivalente aggiunto' : `${n} modelli equivalenti aggiunti`);
    /* Idrata direttamente: aggiunge i nuovi figli alla lista esistente */
    setRebrandInfo(prev => prev ? {
      ...prev,
      children: [
        ...prev.children,
        ...selections.map(sel => ({
          id: sel.id,
          brand: sel.brand,
          model: sel.model,
          categorieEscluse,
        })),
      ],
    } : null);
  }

  /* Imposta il padre — aggiornamento diretto dello stato (no flicker) */
  async function handleSetParent(selections: RebrandSelection[], categorieEscluse: string[]) {
    const sel = selections[0];
    if (!sel) return;
    await addRebrandChild(sel.id, machine.id, categorieEscluse);
    toast.success('Modello produttore associato');
    setRebrandInfo(prev => prev ? {
      ...prev,
      parent: { id: sel.id, brand: sel.brand, model: sel.model, categorieEscluse },
    } : null);
  }

  /* Rimuove un figlio — ottimistico (immediato, ripristina su errore) */
  async function handleRemoveChild(childId: string) {
    setRebrandInfo(prev => prev ? {
      ...prev,
      children: prev.children.filter(c => c.id !== childId),
    } : null);
    try {
      await removeRebrandChild(machine.id, childId);
      toast.success('Modello equivalente rimosso');
    } catch (e: any) {
      toast.error('Errore: ' + (e?.response?.data?.detail ?? e?.message ?? 'unknown'));
      loadRebrands(); // ripristina lo stato corretto
    }
  }

  /* Scollega il padre — ottimistico */
  async function handleDetachSelf() {
    if (!rebrandInfo?.parent) return;
    const parentId = rebrandInfo.parent.id;
    setRebrandInfo(prev => prev ? { ...prev, parent: null } : null);
    try {
      await removeRebrandChild(parentId, machine.id);
      toast.success('Associazione produttore rimossa');
    } catch (e: any) {
      toast.error('Errore: ' + (e?.response?.data?.detail ?? e?.message ?? 'unknown'));
      loadRebrands(); // ripristina lo stato corretto
    }
  }

  async function handleAddArticle(item: { id: string; primary: string }) {
    setArtAddBusy(true);
    try {
      await createAssociations({ skus: [item.id], machineIds: [machine.id] });
      toast.success(`Articolo ${item.id} associato`);
      setArtAddOpen(false); setArtAddQuery(''); setArtAddResults([]);
      loadArticles();
    } catch (e: any) {
      toast.error('Errore: ' + (e?.response?.data?.detail ?? e?.message ?? 'unknown'));
    } finally { setArtAddBusy(false); }
  }

  async function handleRemoveArticles() {
    if (!confirmRemoveCodes) return;
    setRemoving(true);
    try {
      await deleteAssociations(confirmRemoveCodes, [machine.id]);
      toast.success(`${confirmRemoveCodes.length} associazioni rimosse`);
      setConfirmRemoveCodes(null);
      loadArticles();
    } catch (e: any) {
      toast.error('Errore: ' + (e?.response?.data?.detail ?? e?.message ?? 'unknown'));
    } finally { setRemoving(false); }
  }

  const filteredArticles = useMemo(() => {
    const q = artQuery.trim().toLowerCase();
    if (!q) return articles;
    return articles.filter(a =>
      a.sku.toLowerCase().includes(q) ||
      a.desc.toLowerCase().includes(q) ||
      (a.brand ?? '').toLowerCase().includes(q) ||
      (a.cat ?? '').toLowerCase().includes(q),
    );
  }, [articles, artQuery]);

  /* IDs esclusi nella sub-modale figli (se stessa + già figli) */
  const excludedChildIds = useMemo(
    () => [machine.id, ...(rebrandInfo?.children.map(c => c.id) ?? [])],
    [machine.id, rebrandInfo],
  );

  /* ───────────────────────────── RENDER ───────────────────────── */
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl flex flex-col"
        style={{ height: '90vh' }} data-testid="machine-detail-modal">

        {/* ── Header ── */}
        <div className="flex-none px-6 py-4 border-b border-[#e4e4e1] flex items-center gap-3">
          <Package className="w-4 h-4 text-[#2b3a8f] flex-none" />
          <div className="flex-1 min-w-0">
            <h2 className="text-[17px] font-semibold text-[#1f1f1d] truncate">
              {machine.brand} <span className="font-normal text-[#5c5c57]">{machine.model}</span>
            </h2>
            <p className="text-[12px] font-mono text-[#5c5c57]">ID: {machine.id.slice(-12)}</p>
          </div>
          <button onClick={onClose} data-testid="machine-detail-close"
            className="text-[#5c5c57] hover:text-[#3a3a36] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* ── Sezioni superiori scrollabili ── */}
        <div className="flex-none overflow-y-auto border-b border-[#e4e4e1]"
          style={{ maxHeight: 'calc(90vh - 300px)' }}>

          {/* ─ Sezione Modello ─ */}
          <div className="px-6 py-4 border-b border-[#f0f0ed]">
            <div className="text-[11px] font-bold tracking-[0.12em] text-[#5c5c57] font-mono mb-3">
              MODELLO
            </div>
            <div className="grid grid-cols-[1fr_1fr_1.4fr] gap-3 items-end">
              <Combobox label="Marca" value={editBrand} onChange={setEditBrand}
                options={marche} placeholder="Seleziona marca…" testId="edit-brand" />
              <Combobox label="Tipo macchina" value={editTipo} onChange={setEditTipo}
                options={tipologie} placeholder="Seleziona tipologia…" testId="edit-tipo" />
              <div>
                <div className="text-[13px] font-semibold text-[#5c5c57] mb-1.5">Nome modello</div>
                <div className="flex gap-2">
                  <input value={editNome} onChange={e => setEditNome(e.target.value)}
                    data-testid="edit-nome"
                    className="flex-1 px-2.5 py-[7px] border border-[#d8d8d4] rounded-[7px] text-[14px]
                      focus:outline-none focus:border-[#2b3a8f] transition-colors"
                    placeholder="Nome modello…" />
                  <button onClick={handleSave} disabled={saving || !isDirty}
                    data-testid="save-machine-btn"
                    className="px-3 py-[7px] rounded-[7px] bg-[#2b3a8f] text-white text-[14px] font-semibold
                      hover:bg-[#243279] disabled:opacity-40 flex items-center gap-1.5 flex-none transition-colors">
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    Salva
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* ─ Sezione Modello Produttore ─ */}
          <div className="px-6 py-4 border-b border-[#f0f0ed]">
            {/* Header con pulsante a destra */}
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] font-bold tracking-[0.12em] text-[#5c5c57] font-mono">
                MODELLO PRODUTTORE
              </div>
              {/* Mostra "Associa produttore" solo se: caricamento completato, nessun padre, nessun figlio */}
              {!rebrandLoading && !rebrandInfo?.parent && canHaveParent && (
                <button onClick={() => setAddParentOpen(true)} data-testid="associate-parent-btn"
                  className="text-[13px] text-[#2b3a8f] border border-[#c3cdf2] bg-[#eef1fb]
                    px-2.5 py-1.5 rounded-[7px] hover:bg-[#e0e6f8] font-semibold flex items-center gap-1
                    transition-colors">
                  <Link2 className="w-3 h-3" /> Associa produttore
                </button>
              )}
            </div>

            {rebrandLoading ? (
              <div className="flex items-center gap-2 text-[14px] text-[#5c5c57]">
                <Loader2 className="w-3 h-3 animate-spin" /> Caricamento…
              </div>
            ) : rebrandInfo?.parent ? (
              /* Mostra sempre il produttore associato, se esiste, indipendentemente
                 dalla regola di mutua esclusività (che riguarda solo le NUOVE associazioni) */
              <ParentRow
                parent={rebrandInfo.parent}
                onDetach={handleDetachSelf}
                onReplace={() => setAddParentOpen(true)}
              />
            ) : !canHaveParent ? (
              /* Ha già figli → non può avere un padre */
              <div className="text-[13px] text-[#5c5c57] italic bg-purple-50 border border-purple-100
                rounded-[7px] px-3 py-2">
                Questa macchina ha modelli equivalenti propri — non può essere equivalente di un'altra.
              </div>
            ) : (
              <div className="text-[14px] text-[#5c5c57] italic">
                Nessun modello produttore associato.
              </div>
            )}
          </div>

          {/* ─ Sezione Modelli Equivalenti ─ */}
          <div className="px-6 py-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] font-bold tracking-[0.12em] text-[#5c5c57] font-mono">
                MODELLI EQUIVALENTI
                {rebrandInfo && rebrandInfo.children.length > 0 && (
                  <span className="ml-2 bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded text-[12px]">
                    {rebrandInfo.children.length}
                  </span>
                )}
              </div>
              {/* Mostra "Aggiungi" solo se: caricamento completato, nessun padre */}
              {!rebrandLoading && canHaveChildren && (
                <button onClick={() => setAddChildOpen(true)} data-testid="add-rebrand-child-btn"
                  className="text-[13px] font-semibold text-[#2b3a8f] border border-[#c3cdf2] bg-[#eef1fb]
                    px-2.5 py-1.5 rounded-[7px] hover:bg-[#e0e6f8] flex items-center gap-1 transition-colors">
                  <Plus className="w-3 h-3" /> Aggiungi modello equivalente
                </button>
              )}
            </div>

            {rebrandLoading ? (
              <div className="text-[14px] text-[#5c5c57]">Caricamento…</div>
            ) : rebrandInfo && rebrandInfo.children.length > 0 ? (
              /* Mostra sempre i modelli equivalenti esistenti, indipendentemente
                 dalla regola di mutua esclusività (che riguarda solo le NUOVE associazioni) */
              <ChildrenTable
                children={rebrandInfo.children}
                onRemove={handleRemoveChild}
              />
            ) : !canHaveChildren ? (
              /* Ha già un padre → non può avere figli */
              <div className="text-[13px] text-[#5c5c57] italic bg-indigo-50 border border-indigo-100
                rounded-[7px] px-3 py-2">
                Questa macchina è già un modello equivalente — non può avere propri equivalenti.
              </div>
            ) : (
              <div className="text-[14px] text-[#5c5c57] italic">
                Nessun modello equivalente configurato.
              </div>
            )}
          </div>
        </div>

        {/* ─ Sezione Articoli (occupa lo spazio restante) ─ */}
        <div className="flex-1 min-h-0 flex flex-col">

          {/* Articles toolbar */}
          <div className="flex-none px-6 py-2.5 border-b border-[#f0f0ed] bg-[#fafafa]
            flex items-center gap-2 flex-wrap">
            <div className="text-[11px] font-bold tracking-[0.12em] text-[#5c5c57] font-mono flex-none">
              ARTICOLI ASSOCIATI
            </div>
            <span className="text-[12px] font-mono text-[#5c5c57] flex-none">
              {articles.length} totali
            </span>
            <div className="flex-1" />
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 border border-[#d8d8d4]
              rounded-[7px] bg-white w-52">
              <Search className="w-3 h-3 text-[#5c5c57] flex-none" />
              <input value={artQuery} onChange={e => setArtQuery(e.target.value)}
                placeholder="cerca articolo…"
                className="flex-1 text-[13px] outline-none bg-transparent placeholder:text-[#5c5c57]" />
            </div>
            <Popover open={artAddOpen} onOpenChange={o => { setArtAddOpen(o); if (!o) { setArtAddQuery(''); setArtAddResults([]); } }}>
              <PopoverTrigger asChild>
                <button data-testid="art-add-btn"
                  className="px-2.5 py-1.5 rounded-[7px] bg-[#2b3a8f] text-white text-[13px]
                    font-semibold hover:bg-[#243279] flex items-center gap-1 flex-none transition-colors">
                  <Plus className="w-3 h-3" /> Nuovo
                </button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-[440px]" align="end" sideOffset={4}>
                <Command shouldFilter={false}>
                  <CommandInput autoFocus placeholder="cerca per SKU, marca, descrizione…"
                    value={artAddQuery} onValueChange={setArtAddQuery} />
                  <CommandList className="max-h-[280px]">
                    {artAddQuery.length < 2 ? (
                      <CommandEmpty>Digita almeno 2 caratteri.</CommandEmpty>
                    ) : artAddLoading ? (
                      <CommandEmpty>
                        <span className="inline-flex items-center gap-1.5">
                          <Loader2 className="w-3 h-3 animate-spin" /> Ricerca…
                        </span>
                      </CommandEmpty>
                    ) : artAddResults.length === 0 ? (
                      <CommandEmpty>Nessun risultato.</CommandEmpty>
                    ) : (
                      artAddResults.map(r => (
                        <CommandItem key={r.id} value={`${r.id}-${r.primary}`}
                          onSelect={() => !artAddBusy && handleAddArticle(r)}>
                          <div className="flex items-center gap-2 w-full min-w-0"
                            data-testid={`art-add-opt-${r.id}`}>
                            <span className="font-mono text-[13px] text-[#2b3a8f] font-semibold w-20 truncate flex-none">
                              {r.id}
                            </span>
                            <span className="flex-1 truncate text-[14px] text-[#1f1f1d]">{r.primary}</span>
                            <span className="text-[12px] font-mono text-[#5c5c57] flex-none">{r.secondary}</span>
                          </div>
                        </CommandItem>
                      ))
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Articles column header */}
          <div className="flex-none px-6 py-1.5 bg-[#fafafa] border-b border-[#e4e4e1]
            grid grid-cols-[90px_1fr_90px_30px] gap-2">
            {['SKU', 'DESCRIZIONE', 'CATEGORIA', ''].map((h, i) => (
              <div key={i} className="text-[11px] font-bold tracking-[0.08em] text-[#5c5c57] font-mono">{h}</div>
            ))}
          </div>

          {/* Articles list */}
          <div className="flex-1 overflow-y-auto">
            {artLoading ? (
              <div className="px-6 py-10 flex items-center justify-center gap-2 text-[14px] text-[#5c5c57]">
                <Loader2 className="w-4 h-4 animate-spin" /> Caricamento…
              </div>
            ) : filteredArticles.length === 0 ? (
              <div className="px-6 py-10 text-center text-[14px] text-[#5c5c57]">
                {artQuery
                  ? `Nessun articolo per "${artQuery}".`
                  : 'Nessun articolo associato a questa macchina.'}
              </div>
            ) : (
              filteredArticles.map((a, i) => (
                <ArticleRow
                  key={`${a.sku}-${i}`}
                  article={a}
                  onRemove={() => setConfirmRemoveCodes([a.sku])}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* ── Sub-modal: aggiungi figli (multi-select + exclude_parents) ── */}
      {addChildOpen && (
        <AddRebrandModal
          open={addChildOpen}
          onClose={() => setAddChildOpen(false)}
          title="Aggiungi modello equivalente"
          excludeIds={excludedChildIds}
          multiSelect
          excludeParents
          onConfirm={handleAddChild}
        />
      )}

      {/* ── Sub-modal: imposta padre (single-select) ── */}
      {addParentOpen && (
        <AddRebrandModal
          open={addParentOpen}
          onClose={() => setAddParentOpen(false)}
          title="Associa modello produttore"
          excludeIds={[machine.id]}
          onConfirm={handleSetParent}
        />
      )}

      {/* ── Confirm remove articles ── */}
      <AlertDialog
        open={confirmRemoveCodes !== null}
        onOpenChange={o => { if (!o && !removing) setConfirmRemoveCodes(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rimuovi associazione?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai rimuovendo {confirmRemoveCodes?.length ?? 0} associazioni articolo.
              L'operazione è irreversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removing}>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveArticles} disabled={removing}
              className="bg-[#8a3b3b] hover:bg-[#722f2f]">
              {removing ? 'Rimozione…' : 'Sì, rimuovi'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Sub-components
───────────────────────────────────────────────────────────────── */

function ParentRow({ parent, onDetach, onReplace }: {
  parent: RebrandMachine;
  onDetach: () => void;
  onReplace: () => void;
}) {
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200
        text-indigo-700 rounded-lg px-3 py-2 text-[14px] font-semibold">
        <GitBranch className="w-3.5 h-3.5 flex-none" />
        <span className="font-bold">{parent.brand}</span>
        <span className="text-indigo-500 font-normal">—</span>
        <span>{parent.model}</span>
      </div>
      <CatEscluseBadge cats={parent.categorieEscluse} />
      <button onClick={onReplace} data-testid="replace-parent-btn"
        className="text-[13px] text-[#2b3a8f] border border-[#c3cdf2] bg-[#eef1fb]
          px-2.5 py-1.5 rounded-[7px] hover:bg-[#e0e6f8] font-semibold flex items-center gap-1
          transition-colors">
        <Link2 className="w-3 h-3" /> Cambia
      </button>
      <button onClick={onDetach} data-testid="detach-parent-btn"
        className="text-[13px] text-[#8a3b3b] border border-[#e8c0c0] bg-[#fdf5f5]
          px-2.5 py-1.5 rounded-[7px] hover:bg-[#fce8e8] font-semibold flex items-center gap-1
          transition-colors">
        <Link2Off className="w-3 h-3" /> Elimina associazione
      </button>
    </div>
  );
}

function ChildrenTable({ children, onRemove }: {
  children: RebrandMachine[];
  onRemove: (id: string) => void;
}) {
  return (
    <div className="border border-[#e4e4e1] rounded-[9px] overflow-hidden">
      <div className="grid grid-cols-[1fr_1.8fr_180px_36px] bg-[#fafafa] border-b border-[#e4e4e1]">
        {['MARCA', 'MODELLO', 'CAT. ESCLUSE', ''].map((h, i) => (
          <div key={i} className="py-2 px-3 text-[11px] font-bold tracking-[0.08em] text-[#5c5c57] font-mono">{h}</div>
        ))}
      </div>
      <div className="max-h-[160px] overflow-y-auto">
        {children.map(c => (
          <div key={c.id} data-testid={`equiv-row-${c.id}`}
            className="grid grid-cols-[1fr_1.8fr_180px_36px] border-b border-[#f1f1ee]
              last:border-0 hover:bg-[#fafafa] items-center">
            <div className="py-2 px-3 text-[14px] text-[#5c5c57] truncate">{c.brand}</div>
            <div className="py-2 px-3 text-[14px] font-semibold text-[#1f1f1d] truncate">{c.model}</div>
            <div className="py-2 px-3">
              <CatEscluseBadge cats={c.categorieEscluse} />
            </div>
            <div className="py-2 px-1.5">
              <button onClick={() => onRemove(c.id)} data-testid={`remove-child-${c.id}`}
                className="text-[#5c5c57] hover:text-[#8a3b3b] p-1 rounded
                  hover:bg-[#fdf5f5] transition-colors">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ArticleRow({ article, onRemove }: {
  article: MachineArticle;
  onRemove?: () => void;
}) {
  return (
    <div className="grid grid-cols-[90px_1fr_90px_30px] gap-2 px-6 py-1.5
      border-b border-[#f1f1ee] last:border-0 hover:bg-[#fafafa] items-center"
      data-testid={`art-row-${article.sku}`}>
      <div className="text-[13px] font-mono font-semibold text-[#1f1f1d] truncate">{article.sku}</div>
      <div className="text-[13px] text-[#3a3a36] truncate" title={article.desc}>{article.desc}</div>
      <div className="text-[12px] text-[#6d6d68] truncate">{article.cat || '—'}</div>
      <div>
        {onRemove && (
          <button onClick={onRemove} data-testid={`art-remove-${article.sku}`}
            className="text-[#5c5c57] hover:text-[#8a3b3b] p-0.5 rounded
              hover:bg-[#fdf5f5] transition-colors">
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}
